import { useCallback, useMemo, useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { AttachmentSelector } from "../../Project/ProjectViewPage/components/sections/ProjectViewAlbums/AttachmentSelector/AttachmentSelector.tsx"
import { DeleteAlbumModal } from "../../Project/ProjectViewPage/components/sections/ProjectViewAlbums/DeleteAlbum/DeleteAlbumModal.tsx"
import { EditAlbumModal } from "../../Project/ProjectViewPage/components/sections/ProjectViewAlbums/EditAlbum/EditAlbumModal.tsx"
import {
  buildAlbumMenuItems,
  type TranslateFn
} from "../helpers/album-menu-items.tsx"
import {
  addAlbumAttachments,
  deleteAlbum,
  updateAlbum
} from "../redux/album.thunks.ts"
import type {
  Album,
  AlbumDeleteFilesMode,
  AlbumListItem
} from "../types/album.types.ts"

const favoriteKeyFor = (album: AlbumListItem) => `album:${album.id}`

interface UseAlbumActionsControllerArgs {
  projectId: string
  userId: string
  editAllowed: boolean
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
  isFavorite: (key: string) => boolean
  toggleFavorite: (key: string) => void
  // Called after a write actually changes something the caller's own list
  // holds (add files) — the caller decides whether/how to refetch; renaming,
  // sharing and deleting a row are already reflected via the shared reducer.
  onChanged?: () => void
}

// Shared state + dispatch behind the album ⋯ menu (rename, share/visibility,
// add files, favorite, delete) and its three modals — used identically by
// the Albums directory and by every album card/list rendered inside the main
// Files screen, so the actions and their confirmations never drift apart.
export function useAlbumActionsController({
  projectId,
  userId,
  editAllowed,
  deleteAllowed,
  deleteForeignAllowed,
  isFavorite,
  toggleFavorite,
  onChanged
}: UseAlbumActionsControllerArgs) {
  const dispatch = useAppDispatch()

  const [editingAlbum, setEditingAlbum] = useState<AlbumListItem | null>(null)
  const [deletingAlbum, setDeletingAlbum] = useState<AlbumListItem | null>(null)
  const [isDeletingAlbum, setIsDeletingAlbum] = useState(false)
  const [addingFilesAlbum, setAddingFilesAlbum] =
    useState<AlbumListItem | null>(null)

  const handleEditSubmit = useCallback(
    async (data: Partial<Album>) => {
      if (!editingAlbum) return
      await dispatch(updateAlbum({ id: editingAlbum.id, data })).unwrap()
      setEditingAlbum(null)
      // Redux's own `albums.results` is patched by the thunk's reducer, but a
      // caller reading albums from its own local state (the Files overview's
      // preview, not Redux) would otherwise keep showing the pre-edit name
      // and visibility until an unrelated refetch happened to run.
      onChanged?.()
    },
    [dispatch, editingAlbum, onChanged]
  )

  const handleDelete = useCallback(
    async (files: AlbumDeleteFilesMode) => {
      if (!deletingAlbum) return
      setIsDeletingAlbum(true)
      try {
        // `unwrap` is the point: a rejected delete otherwise settles as a
        // fulfilled promise and the modal closes over an album still there.
        await dispatch(deleteAlbum({ id: deletingAlbum.id, files })).unwrap()
      } catch {
        return
      } finally {
        setIsDeletingAlbum(false)
      }
      setDeletingAlbum(null)
      onChanged?.()
    },
    [dispatch, deletingAlbum, onChanged]
  )

  const handleAddFiles = useCallback(
    async (attachmentIds: string[]) => {
      if (!addingFilesAlbum) return
      await dispatch(
        addAlbumAttachments({
          id: addingFilesAlbum.id,
          data: { attachment_ids: attachmentIds }
        })
      ).unwrap()
      setAddingFilesAlbum(null)
      onChanged?.()
    },
    [dispatch, addingFilesAlbum, onChanged]
  )

  const menuItemsFor = useCallback(
    (
      album: AlbumListItem,
      onView: (album: AlbumListItem) => void,
      t: TranslateFn
    ) =>
      buildAlbumMenuItems(
        album,
        { userId, editAllowed, deleteAllowed, deleteForeignAllowed },
        {
          onView,
          onEdit: setEditingAlbum,
          onAddFiles: setAddingFilesAlbum,
          onToggleFavorite: (a) => toggleFavorite(favoriteKeyFor(a)),
          onDelete: setDeletingAlbum
        },
        isFavorite(favoriteKeyFor(album)),
        t
      ),
    [
      userId,
      editAllowed,
      deleteAllowed,
      deleteForeignAllowed,
      isFavorite,
      toggleFavorite
    ]
  )

  const modals = useMemo(
    () => (
      <>
        {editingAlbum && (
          <EditAlbumModal
            album={editingAlbum}
            onClose={() => setEditingAlbum(null)}
            onSubmit={handleEditSubmit}
          />
        )}
        {deletingAlbum && (
          <DeleteAlbumModal
            albumName={deletingAlbum.name}
            fileCount={deletingAlbum.attachments_count ?? 0}
            isDeleting={isDeletingAlbum}
            onClose={() => setDeletingAlbum(null)}
            onConfirm={(files) => void handleDelete(files)}
          />
        )}
        {addingFilesAlbum && (
          <AttachmentSelector
            projectId={projectId}
            existingAttachmentIds={(addingFilesAlbum.attachments ?? []).map(
              (a) => a.id
            )}
            onClose={() => setAddingFilesAlbum(null)}
            onSubmit={handleAddFiles}
          />
        )}
      </>
    ),
    [
      editingAlbum,
      handleEditSubmit,
      deletingAlbum,
      isDeletingAlbum,
      handleDelete,
      addingFilesAlbum,
      projectId,
      handleAddFiles
    ]
  )

  return {
    menuItemsFor,
    isFavoriteAlbum: (a: AlbumListItem) => isFavorite(favoriteKeyFor(a)),
    modals
  }
}
